import React, { useState, useEffect, useRef, useCallback } from 'react';

// Game settings
const BOARD_SIZE = 30; // Number of cells per row/column (changed from GRID_SIZE)
const CELL_SIZE = 20; // Base size of each cell in pixels (for conceptual use, actual size is dynamic)
const INITIAL_SPEED = 180; // Slightly slower initial speed for easier start
const SPEED_INCREMENT_SCORE = 5; // Every X points, speed increases
const SPEED_DECREMENT_AMOUNT = 12; // How much speed decreases (game gets faster, adjusted for new speed)

// Helper function to draw a rounded rectangle
function drawRoundedRect(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

// Main App component for the Snake game
function Snackgame() {
  const canvasRef = useRef(null);
  const gameContainerRef = useRef(null); // Ref for the game container to measure its size
  const [snake, setSnake] = useState([{ x: Math.floor(BOARD_SIZE / 2), y: Math.floor(BOARD_SIZE / 2) }]); // Initial snake position adjusted for new grid
  const [food, setFood] = useState({ x: 5, y: 5 }); // Initial food position
  const [direction, setDirection] = useState('right'); // 'up', 'down', 'left', 'right'
  const [score, setScore] = useState(0);
  const [gameSpeed, setGameSpeed] = useState(INITIAL_SPEED);
  const [isPlaying, setIsPlaying] = useState(false);
  const [gameOver, setGameOver] = useState(true);
  const [statusMessage, setStatusMessage] = useState('Press Start to Play!');
  const [canvasWidth, setCanvasWidth] = useState(0); // Dynamic canvas width (drawing buffer)
  const [canvasHeight, setCanvasHeight] = useState(0); // Dynamic canvas height (drawing buffer)

  const gameLoopRef = useRef(null);
  const lastDirectionRef = useRef('right'); // To prevent immediate 180-degree turns

  // Audio references for game sounds
  const eatSoundRef = useRef(null);
  const gameOverSoundRef = useRef(null);

  // Initialize audio elements on component mount
  useEffect(() => {
    // Only load audio if available in the environment
    if (typeof Audio !== 'undefined') {
      eatSoundRef.current = new Audio('https://www.soundjay.com/buttons/beep-07.mp3');
      gameOverSoundRef.current = new Audio('https://www.soundjay.com/misc/fail-buzzer-01.mp3');
    }
  }, []);

  // Function to play a sound
  const playSound = useCallback((soundRef) => {
    if (soundRef.current) {
      soundRef.current.currentTime = 0;
      soundRef.current.play().catch(e => console.error("Error playing sound:", e));
    }
  }, []);

  // Function to generate new food position, ensuring it doesn't overlap with the snake
  const generateFood = useCallback((currentSnake) => {
    let newFood;
    let collision;
    do {
      newFood = {
        x: Math.floor(Math.random() * BOARD_SIZE),
        y: Math.floor(Math.random() * BOARD_SIZE),
      };
      collision = currentSnake.some(segment => segment.x === newFood.x && segment.y === newFood.y);
    } while (collision);
    setFood(newFood);
  }, []);

  // Game initialization function: Resets game state and prepares for a new round
  const initializeGame = useCallback(() => {
    setSnake([{ x: Math.floor(BOARD_SIZE / 2), y: Math.floor(BOARD_SIZE / 2) }]); // Reset snake to initial position
    setDirection('right');
    lastDirectionRef.current = 'right';
    setScore(0);
    setGameSpeed(INITIAL_SPEED);
    generateFood([{ x: Math.floor(BOARD_SIZE / 2), y: Math.floor(BOARD_SIZE / 2) }]);
    setIsPlaying(true);
    setGameOver(false);
    setStatusMessage('Use arrow keys to move!');
  }, [generateFood]);

  // Effect hook for handling keyboard input (arrow keys)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isPlaying) return; // Only process input if game is playing
      const newDirection = e.key.replace('Arrow', '').toLowerCase();
      const lastDir = lastDirectionRef.current;

      // Prevent immediate 180-degree turns
      if (
        (newDirection === 'up' && lastDir !== 'down') ||
        (newDirection === 'down' && lastDir !== 'up') ||
        (newDirection === 'left' && lastDir !== 'right') ||
        (newDirection === 'right' && lastDir !== 'left')
      ) {
        setDirection(newDirection);
        lastDirectionRef.current = newDirection;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPlaying]); // Depend on isPlaying to disable input when not active

  // Main game loop effect: Handles snake movement, collisions, and food logic
  useEffect(() => {
    if (!isPlaying || gameOver) {
      clearInterval(gameLoopRef.current);
      return;
    }

    clearInterval(gameLoopRef.current);
    gameLoopRef.current = setInterval(() => {
      setSnake(prevSnake => {
        const newSnake = [...prevSnake];
        const head = { ...newSnake[0] };

        switch (direction) {
          case 'up':
            head.y -= 1;
            break;
          case 'down':
            head.y += 1;
            break;
          case 'left':
            head.x -= 1;
            break;
            case 'right':
            head.x += 1;
            break;
          default:
            break;
        }

        // Check for boundary collisions
        if (
          head.x < 0 || head.x >= BOARD_SIZE ||
          head.y < 0 || head.y >= BOARD_SIZE
        ) {
          setGameOver(true);
          setIsPlaying(false);
          setStatusMessage(`Game Over! Final Score: ${score}`);
          playSound(gameOverSoundRef);
          return prevSnake;
        }

        // Check for self-collision
        for (let i = 1; i < newSnake.length; i++) {
          if (head.x === newSnake[i].x && head.y === newSnake[i].y) {
            setGameOver(true);
            setIsPlaying(false);
            setStatusMessage(`Game Over! Final Score: ${score}`);
            playSound(gameOverSoundRef);
            return prevSnake;
          }
        }

        // Check for food collision
        if (head.x === food.x && head.y === food.y) {
          setScore(prevScore => prevScore + 1);
          playSound(eatSoundRef);
          generateFood(newSnake);
          if ((score + 1) % SPEED_INCREMENT_SCORE === 0) {
            setGameSpeed(prevSpeed => Math.max(80, prevSpeed - SPEED_DECREMENT_AMOUNT)); // Min speed 80ms
          }
        } else {
          newSnake.pop();
        }

        newSnake.unshift(head);
        return newSnake;
      });
    }, gameSpeed);

    return () => clearInterval(gameLoopRef.current);
  }, [isPlaying, gameOver, direction, food, score, gameSpeed, generateFood, playSound]);

  // Effect hook for drawing on the canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear the entire canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate actual cell size based on current canvas dimensions
    const CELL_SIZE_ACTUAL_WIDTH = canvas.width / BOARD_SIZE;
    const CELL_SIZE_ACTUAL_HEIGHT = canvas.height / BOARD_SIZE;
    const SEGMENT_RADIUS = Math.min(CELL_SIZE_ACTUAL_WIDTH, CELL_SIZE_ACTUAL_HEIGHT) / 4; // Radius for rounded corners

    // Draw snake
    snake.forEach((segment, index) => {
      // Head color and body color, with a subtle darker shade for the head
      ctx.fillStyle = index === 0 ? '#2E7D32' : '#4CAF50'; // Darker green for head, normal green for body
      ctx.strokeStyle = '#1B5E20'; // Dark border for snake
      ctx.lineWidth = 1;

      // Draw rounded rectangle for each segment
      drawRoundedRect(ctx,
        segment.x * CELL_SIZE_ACTUAL_WIDTH,
        segment.y * CELL_SIZE_ACTUAL_HEIGHT,
        CELL_SIZE_ACTUAL_WIDTH,
        CELL_SIZE_ACTUAL_HEIGHT,
        SEGMENT_RADIUS
      );
      ctx.fill();
      ctx.stroke();

      // Draw eyes on the snake's head
      if (index === 0) {
        ctx.fillStyle = 'white';
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 1;
        const eyeSize = Math.min(CELL_SIZE_ACTUAL_WIDTH, CELL_SIZE_ACTUAL_HEIGHT) / 8;
        const eyeOffset = Math.min(CELL_SIZE_ACTUAL_WIDTH, CELL_SIZE_ACTUAL_HEIGHT) / 4;

        let eye1X, eye1Y, eye2X, eye2Y;

        switch (direction) {
          case 'up':
            eye1X = segment.x * CELL_SIZE_ACTUAL_WIDTH + CELL_SIZE_ACTUAL_WIDTH / 4;
            eye1Y = segment.y * CELL_SIZE_ACTUAL_HEIGHT + eyeOffset;
            eye2X = segment.x * CELL_SIZE_ACTUAL_WIDTH + 3 * CELL_SIZE_ACTUAL_WIDTH / 4;
            eye2Y = segment.y * CELL_SIZE_ACTUAL_HEIGHT + eyeOffset;
            break;
          case 'down':
            eye1X = segment.x * CELL_SIZE_ACTUAL_WIDTH + CELL_SIZE_ACTUAL_WIDTH / 4;
            eye1Y = segment.y * CELL_SIZE_ACTUAL_HEIGHT + CELL_SIZE_ACTUAL_HEIGHT - eyeOffset;
            eye2X = segment.x * CELL_SIZE_ACTUAL_WIDTH + 3 * CELL_SIZE_ACTUAL_WIDTH / 4;
            eye2Y = segment.y * CELL_SIZE_ACTUAL_HEIGHT + CELL_SIZE_ACTUAL_HEIGHT - eyeOffset;
            break;
          case 'left':
            eye1X = segment.x * CELL_SIZE_ACTUAL_WIDTH + eyeOffset;
            eye1Y = segment.y * CELL_SIZE_ACTUAL_HEIGHT + CELL_SIZE_ACTUAL_HEIGHT / 4;
            eye2X = segment.x * CELL_SIZE_ACTUAL_WIDTH + eyeOffset;
            eye2Y = segment.y * CELL_SIZE_ACTUAL_HEIGHT + 3 * CELL_SIZE_ACTUAL_HEIGHT / 4;
            break;
          case 'right':
            eye1X = segment.x * CELL_SIZE_ACTUAL_WIDTH + CELL_SIZE_ACTUAL_WIDTH - eyeOffset;
            eye1Y = segment.y * CELL_SIZE_ACTUAL_HEIGHT + CELL_SIZE_ACTUAL_HEIGHT / 4;
            eye2X = segment.x * CELL_SIZE_ACTUAL_WIDTH + CELL_SIZE_ACTUAL_WIDTH - eyeOffset;
            eye2Y = segment.y * CELL_SIZE_ACTUAL_HEIGHT + 3 * CELL_SIZE_ACTUAL_HEIGHT / 4;
            break;
          default:
            break;
        }

        ctx.beginPath();
        ctx.arc(eye1X, eye1Y, eyeSize, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(eye2X, eye2Y, eyeSize, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Draw pupils
        ctx.fillStyle = 'black';
        const pupilSize = eyeSize / 2;
        const pupilOffset = eyeSize / 2; // Offset from eye center

        let pupil1X, pupil1Y, pupil2X, pupil2Y;

        switch (direction) {
          case 'up':
            pupil1X = eye1X;
            pupil1Y = eye1Y - pupilOffset;
            pupil2X = eye2X;
            pupil2Y = eye2Y - pupilOffset;
            break;
          case 'down':
            pupil1X = eye1X;
            pupil1Y = eye1Y + pupilOffset;
            pupil2X = eye2X;
            pupil2Y = eye2Y + pupilOffset;
            break;
          case 'left':
            pupil1X = eye1X - pupilOffset;
            pupil1Y = eye1Y;
            pupil2X = eye2X - pupilOffset;
            pupil2Y = eye2Y;
            break;
          case 'right':
            pupil1X = eye1X + pupilOffset;
            pupil1Y = eye1Y;
            pupil2X = eye2X + pupilOffset;
            pupil2Y = eye2Y;
            break;
          default:
            break;
        }

        ctx.beginPath();
        ctx.arc(pupil1X, pupil1Y, pupilSize, 0, Math.PI * 2);
        ctx.fill();

        ctx.beginPath();
        ctx.arc(pupil2X, pupil2Y, pupilSize, 0, Math.PI * 2);
        ctx.fill();
      }
    });

    // Draw food (as a stylized apple)
    ctx.fillStyle = '#FF4500'; // Red-orange for apple
    ctx.strokeStyle = '#B30000'; // Darker red border
    ctx.lineWidth = 2;
    // Draw apple body
    ctx.beginPath();
    ctx.arc(
      food.x * CELL_SIZE_ACTUAL_WIDTH + CELL_SIZE_ACTUAL_WIDTH / 2,
      food.y * CELL_SIZE_ACTUAL_HEIGHT + CELL_SIZE_ACTUAL_HEIGHT / 2,
      CELL_SIZE_ACTUAL_WIDTH / 2 * 0.8, // Slightly smaller than cell
      0,
      Math.PI * 2
    );
    ctx.fill();
    ctx.stroke();

    // Draw apple stem
    ctx.beginPath();
    ctx.moveTo(food.x * CELL_SIZE_ACTUAL_WIDTH + CELL_SIZE_ACTUAL_WIDTH / 2, food.y * CELL_SIZE_ACTUAL_HEIGHT + CELL_SIZE_ACTUAL_HEIGHT / 2 - CELL_SIZE_ACTUAL_HEIGHT * 0.4);
    ctx.lineTo(food.x * CELL_SIZE_ACTUAL_WIDTH + CELL_SIZE_ACTUAL_WIDTH / 2 + CELL_SIZE_ACTUAL_WIDTH * 0.1, food.y * CELL_SIZE_ACTUAL_HEIGHT + CELL_SIZE_ACTUAL_HEIGHT / 2 - CELL_SIZE_ACTUAL_HEIGHT * 0.2);
    ctx.strokeStyle = '#8B4513'; // Brown
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw apple leaf
    ctx.beginPath();
    ctx.ellipse(
      food.x * CELL_SIZE_ACTUAL_WIDTH + CELL_SIZE_ACTUAL_WIDTH / 2 + CELL_SIZE_ACTUAL_WIDTH * 0.15,
      food.y * CELL_SIZE_ACTUAL_HEIGHT + CELL_SIZE_ACTUAL_HEIGHT / 2 - CELL_SIZE_ACTUAL_HEIGHT * 0.25,
      CELL_SIZE_ACTUAL_WIDTH * 0.15, // horizontal radius
      CELL_SIZE_ACTUAL_HEIGHT * 0.1, // vertical radius
      -Math.PI / 4, // rotation
      0,
      Math.PI * 2
    );
    ctx.fillStyle = '#32CD32'; // Lime green
    ctx.fill();
    ctx.strokeStyle = '#228B22'; // Darker green
    ctx.lineWidth = 1;
    ctx.stroke();

  }, [snake, food, gameOver, canvasWidth, canvasHeight, direction]); // Redraw on snake/food changes or game over

  // Effect hook for responsive canvas size adjustment to fit the screen
  useEffect(() => {
    const handleResize = () => {
      const gameContainer = gameContainerRef.current;
      if (!gameContainer) return;

      // Calculate available space within the game container
      const containerWidth = gameContainer.clientWidth;
      const containerHeight = gameContainer.clientHeight;

      // Determine the largest possible square size that fits within the container,
      // The canvas itself will remain square for proper grid rendering.
      const maxPossibleSquareSize = Math.min(containerWidth, containerHeight);
      
      // Ensure the canvas size is a multiple of BOARD_SIZE for crisp pixel rendering
      // Also, set a minimum size to ensure playability on very small screens

      setCanvasWidth(500);
      setCanvasHeight(500);
    };

    const observer = new ResizeObserver(() => {
      handleResize();
    });

    if (gameContainerRef.current) {
      observer.observe(gameContainerRef.current);
    }
    handleResize(); // Initial call

    return () => {
      if (gameContainerRef.current) {
        observer.unobserve(gameContainerRef.current);
      }
    };
  }, []);

  return (
    // Outer container: Fills the available space from TestLayout's Outlet, centers content
    <div className=" w-full flex items-center justify-center font-inter p-4 bg-gradient-to-br from-teal-200 via-lime-300 to-green-200 overflow-hidden">
      {/* Game container: The main visual card for the game */}
      <div
        ref={gameContainerRef}
        className="relative bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-6 sm:p-8 md:p-10 rounded-3xl shadow-2xl flex flex-col items-center justify-between border border-gray-100 backdrop-blur-sm game-card h-[calc(100%-2rem)] w-[calc(100%-2rem)] max-w-screen-xl mx-auto overflow-hidden"
      >
        <h1 className="text-xl sm:text-3xl md:text-3xl font-extrabold text-gray-800 mb-2 text-center tracking-tight leading-tight drop-shadow-md text-purple-700">
          🐍 Epic Snake Quest 🍎
        </h1>

        <div className="mb-2 flex flex-col sm:flex-row gap-4 justify-center items-center text-xl sm:text-2xl font-bold text-gray-700 w-full score-speed-container">
          <div className="flex items-center space-x-2 p-2 bg-white rounded-xl shadow-md flex-1 min-w-[120px] justify-center">
            <span className="text-lime-600">Score:</span>
            <span className="text-2xl sm:text-3xl font-extrabold text-lime-700">{score}</span>
          </div>
          <div className="flex items-center space-x-2 p-2 bg-white rounded-xl shadow-md flex-1 min-w-[120px] justify-center">
            <span className="text-blue-600">Speed:</span>
            <span className="text-2xl sm:text-3xl font-extrabold text-blue-700">{Math.floor(1000 / gameSpeed)}</span>
          </div>
        </div>

        <div className="text-1xl sm:text-2xl md:text-2xl font-extrabold mb-4 text-gray-700 text-center animate-fade-in-up status-message">
          {statusMessage}
        </div>

        <canvas
          ref={canvasRef}
          width={canvasWidth}
          height={canvasHeight}
          // Using the user's requested classes and updated sizing for the board
          className="relative border-4 border-green-500 rounded-xl overflow-hidden bg-gray-800 shadow-2xl flex-grow"
          // Explicitly setting CSS width/height to match drawing buffer for consistent rendering
          style={{ width: canvasWidth, height: canvasHeight }} 
        ></canvas>

        <button
          onClick={initializeGame}
          disabled={isPlaying && !gameOver}
          className={`
            mt-6 px-10 py-4 bg-gradient-to-r from-green-600 to-teal-600 text-white font-bold text-xl rounded-full shadow-lg transform transition-all duration-300
            hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-green-400 focus:ring-opacity-75 active:scale-95
            ${isPlaying && !gameOver ? 'opacity-50 cursor-not-allowed' : 'animate-pulse-once'}
          `}
        >
          {isPlaying && !gameOver ? 'Playing...' : 'Start Game'}
        </button>

        {/* Confetti effect */}
        {gameOver && score > 0 && (
          <>
            {Array.from({ length: 120 }).map((_, i) => (
              <div
                key={i}
                className="confetti-piece"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  backgroundColor: `hsl(${Math.random() * 360}, 90%, 70%)`,
                  animationDelay: `${Math.random() * 3}s`,
                  transform: `scale(${0.6 + Math.random() * 0.8}) rotate(${Math.random() * 360}deg)`,
                  width: `${10 + Math.random() * 10}px`,
                  height: `${10 + Math.random() * 10}px`,
                }}
              ></div>
            ))}
          </>
        )}

        {/* Instructions for the game */}
        <div className="mt-6 text-center max-w-md text-gray-700 text-sm p-4 bg-blue-50 rounded-xl shadow-inner border border-blue-100 instructions-container">
          <h3 className="font-bold text-lg mb-2 text-blue-800">How to Play:</h3>
          <p className="text-gray-800 leading-relaxed">
            Use the <span className="font-extrabold text-indigo-700">Arrow Keys</span> (Up, Down, Left, Right) to control the snake.
            Eat the <span className="text-yellow-600 font-extrabold">red apples</span> to grow and increase your score.
            Avoid hitting the walls or your own tail!
            The game speeds up as your score increases, challenging your reflexes!
          </p>
        </div>
      </div>

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap');
        body {
          font-family: 'Inter', sans-serif;
          margin: 0;
          padding: 0;
          overflow: hidden;
        }

        .animate-fade-in-up {
            animation: fadeInScaleUp 0.6s ease-out forwards;
        }

        @keyframes fadeInScaleUp {
            from {
                opacity: 0;
                transform: translateY(30px) scale(0.9);
            }
            to {
                opacity: 1;
                transform: translateY(0) scale(1);
            }
        }

        .animate-pulse-once {
            animation: pulseOnce 1.5s ease-in-out;
        }

        @keyframes pulseOnce {
            0%, 100% {
                transform: scale(1);
            }
            50% {
                transform: scale(1.02);
            }
        }

        .confetti-piece {
          position: absolute;
          border-radius: 50%;
          opacity: 0;
          animation: fall 4s ease-out forwards;
          z-index: 10;
          filter: drop-shadow(2px 2px 2px rgba(0,0,0,0.2));
        }

        @keyframes fall {
          0% {
            opacity: 0;
            transform: translateY(-100px) rotateZ(0deg) scale(0.5);
          }
          10% {
            opacity: 1;
          }
          100% {
            transform: translateY(calc(100vh + 200px)) rotateZ(1080deg) scale(1);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}

export default Snackgame;
